/**
 * Cascara de la aplicacion: header, menu lateral y el hueco donde el router
 * monta la seccion activa.
 *
 * Tres cosas viven aca a proposito, y no en cada pantalla:
 *
 * 1. **El selector de tienda.** Esta arriba de `<Outlet>`, asi que navegar no
 *    lo desmonta y la eleccion no se pierde entre secciones. Si viviera adentro
 *    de una pantalla, cada navegacion lo remontaria y volveria a resolver la
 *    tenencia.
 * 2. **La tenencia como condicion del menu.** Un usuario sin ninguna tienda
 *    concedida no ve un menu de seis secciones que no puede abrir: ve por que
 *    no las ve.
 * 3. **El `<Suspense>`** de las pantallas cargadas por chunk (`lazyScreen.tsx`).
 *    Sin el, una pantalla lazy tira un error de Suspense en vez de mostrar un
 *    "cargando".
 */

import { Suspense } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { useStoreAccess } from "../stores/StoreAccessProvider";
import { StoreSelector } from "../stores/StoreSelector";
import { ApiStatus } from "./ApiStatus";
import { ErrorState, NoStoreAccessState, Spinner } from "./Feedback";
import { SECTIONS } from "./routes";
import { STORE_PARAM, useStoreDeepLink } from "./useStoreDeepLink";
import "./layout.css";

function SectionNav() {
  return (
    <nav className="shell__nav" aria-label="Secciones">
      {SECTIONS.map((section) => (
        <NavLink
          key={section.id}
          to={section.path}
          // `end` solo en la raiz: sin esto, "/" queda activa en toda ruta,
          // porque todas empiezan con "/".
          end={section.path === "/"}
          title={section.hint}
          className={({ isActive }) =>
            isActive ? "nav-link nav-link--active" : "nav-link"
          }
        >
          <span className="nav-link__label">{section.label}</span>
          {section.scope === "global" ? (
            <span className="nav-link__badge" title="No filtra por la tienda seleccionada">
              todas
            </span>
          ) : null}
        </NavLink>
      ))}
    </nav>
  );
}

/**
 * Cartel para un link compartido que apunta a una tienda que este usuario no
 * tiene. Explicito y no una pantalla en blanco: la persona que abrio el link no
 * hizo nada mal, y necesita saber que le falta acceso, no que "no hay datos".
 */
function DeepLinkDenied({ raw, invalid }: { readonly raw: string; readonly invalid: boolean }) {
  return (
    <div className="feedback feedback--error" role="alert">
      <p>
        {invalid ? (
          <>
            <strong>Link invalido.</strong> El parametro <code>{STORE_PARAM}</code> del
            link no es un id de tienda (<code>{raw}</code>).
          </>
        ) : (
          <>
            <strong>Sin acceso a esa tienda.</strong> El link pedia la tienda{" "}
            <code>{raw}</code> y tu usuario no la tiene concedida.
          </>
        )}
      </p>
      <p>
        Podes seguir usando el panel con las tiendas que si tenes: elegi una en el
        selector de arriba.
      </p>
    </div>
  );
}

export function AppLayout() {
  const { session, signOut } = useAuth();
  const { state, reload } = useStoreAccess();
  const deepLink = useStoreDeepLink();

  const accessResolved = state.kind === "ready";
  const hasStores = accessResolved && state.data.length > 0;
  // Durante la resolucion el menu se muestra igual: los links son estaticos y
  // que la cascara salte en cada carga es peor que mostrarlos un instante antes
  // de saber si sirven. Solo se esconde cuando ya sabemos que no hay ninguna.
  const showNav = !accessResolved || hasStores;

  function content() {
    if (state.kind === "error") {
      return <ErrorState error={state.error} onRetry={reload} />;
    }
    if (accessResolved && !hasStores) {
      return <NoStoreAccessState />;
    }
    if (deepLink.kind !== "none") {
      return <DeepLinkDenied raw={deepLink.raw} invalid={deepLink.kind === "invalid"} />;
    }
    return (
      <Suspense fallback={<Spinner label="Cargando pantalla..." />}>
        <Outlet />
      </Suspense>
    );
  }

  return (
    <div className="shell">
      <header className="app__header">
        <div className="app__brand">Barrio Chino</div>
        <StoreSelector />
        <div className="app__spacer" />
        <ApiStatus />
        <span className="app__user">{session?.user.email}</span>
        <button type="button" onClick={() => void signOut()}>
          Salir
        </button>
      </header>

      <div className={showNav ? "shell__body" : "shell__body shell__body--solo"}>
        {showNav ? <SectionNav /> : null}
        <main className="shell__main">{content()}</main>
      </div>
    </div>
  );
}
